import React from 'react'
import { ArrowRight, Check, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { fillTokens, formatPrice, resolveText, splitBold } from './render'
import type { CardBadge, CardBlock, CardContent, CardPlan, LocalizedText } from './types'

/**
 * One plan as the paywall draws it, from card content (a template with the plan's
 * overrides, or the default card). Shared by the paywall and the admin previews, so a
 * preview is what users see.
 *
 * Text is rendered as text: `**bold**` is the only markup. The price always comes from
 * the plan; the card only adds a period label and a note around it.
 */

export interface PlanCardCta {
  disabled?: boolean
  loading?: boolean
  onClick?: () => void
}

interface PlanCardProps {
  plan: CardPlan
  card: CardContent
  /** The language to draw in; per string, English when a translation is missing. */
  language: string
  cta?: PlanCardCta
  className?: string
}

const RichText: React.FC<{ text: string }> = ({ text }) => (
  <>
    {splitBold(text).map((part, i) =>
      part.bold ? (
        <strong key={i} className="font-semibold text-[var(--card-strong)]">
          {part.text}
        </strong>
      ) : (
        <React.Fragment key={i}>{part.text}</React.Fragment>
      ),
    )}
  </>
)

const Badge: React.FC<{ badge: CardBadge; text: string }> = ({ badge, text }) => (
  <div
    className={cn(
      'absolute z-10 whitespace-nowrap rounded-full font-extrabold uppercase',
      badge.position === 'top' ? 'left-1/2 -top-[11px] -translate-x-1/2 px-3 py-[5px] text-[10.5px] tracking-[1.5px]' : 'right-5 top-5 px-2.5 py-1 text-[9.5px] tracking-[1.3px]',
      badge.tone === 'highlight' && 'bg-[#C5F26A] text-[#073529] shadow-[0_4px_12px_rgba(164,212,71,0.4)]',
      badge.tone === 'outline' && 'border-[1.5px] border-[#D6EBDE] bg-white text-[#0F4C3A] shadow-[0_2px_6px_rgba(15,76,58,0.08)]',
      badge.tone === 'subtle' && 'border border-[#D6EBDE] bg-[#F2F8F4] text-[#0F4C3A]',
    )}
  >
    {text}
  </div>
)

/** Space under a block: a title sits close to its tagline, everything else breathes. */
const gapAfter = (block: CardBlock, next: CardBlock | undefined): string => {
  if (block.type === 'title') return next?.type === 'tagline' ? 'mb-1.5' : 'mb-5'
  if (block.type === 'featuresLabel') return 'mb-3.5'
  if (block.type === 'cta') return ''
  return 'mb-5'
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, card, language, cta, className }) => {
  const { i18n } = useTranslation()
  const t = i18n.getFixedT(language)
  const featured = card.variant === 'featured'
  const intervalWord = t(`subscription.card.${plan.interval}`)
  const periodPhrase = t(`subscription.card.${plan.renews ? 'every' : 'forOne'}.${plan.interval}`)
  const text = (value: LocalizedText | undefined) => fillTokens(resolveText(value, language), plan, language, intervalWord, periodPhrase)

  const renderBlock = (block: CardBlock, index: number) => {
    const spacing = gapAfter(block, card.blocks[index + 1])
    switch (block.type) {
      case 'title':
        return (
          <div key={block.id} className={cn('text-xl font-bold leading-tight tracking-[-0.4px]', featured ? 'text-white' : 'text-[#0F4C3A]', spacing)}>
            <RichText text={text(block.text)} />
          </div>
        )
      case 'tagline':
        return (
          <div key={block.id} className={cn('text-[13px] leading-snug', featured ? 'text-white/70' : 'text-[#6B7D72]', spacing)}>
            <RichText text={text(block.text)} />
          </div>
        )
      case 'price': {
        const period = block.periodLabel ? text(block.periodLabel) : t(`subscription.paywall.${plan.renews ? 'per' : 'forOne'}.${plan.interval}`)
        return (
          <div key={block.id} className={spacing}>
            <div className="flex flex-wrap items-baseline gap-1.5">
              <span className={cn('text-[42px] font-extrabold leading-none tracking-[-2px] tabular-nums', featured ? 'text-[#C5F26A]' : 'text-[#0F4C3A]')}>{formatPrice(plan, language)}</span>
              <span className={cn('text-sm font-medium', featured ? 'text-white/65' : 'text-[#6B7D72]')}>{period}</span>
            </div>
            {block.note && (
              <div className={cn('mt-1 text-[11.5px]', featured ? 'text-white/55' : 'text-[#6B7D72]')}>
                <RichText text={text(block.note)} />
              </div>
            )}
          </div>
        )
      }
      case 'divider':
        return <div key={block.id} className={cn('h-px', featured ? 'bg-white/10' : 'bg-[#E8EDE9]', spacing)} />
      case 'featuresLabel':
        return (
          <div key={block.id} className={cn('text-[10.5px] font-bold uppercase tracking-[1.4px]', featured ? 'text-[#C5F26A]' : 'text-[#6B7D72]', spacing)}>
            {text(block.text)}
          </div>
        )
      case 'features':
        return (
          <ul key={block.id} className={cn('flex flex-col gap-3', spacing)}>
            {block.items.map((item, i) => (
              <li key={i} className={cn('flex items-start gap-2.5 text-[13.5px] leading-snug', featured ? 'text-white/90' : 'text-[#364942]')}>
                <span className={cn('mt-px flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full', featured ? 'bg-[#C5F26A] text-[#073529]' : 'bg-[#E8F5EE] text-[#0F4C3A]')}>
                  <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
                </span>
                <span>
                  <RichText text={text(item.text)} />
                  {item.tag && (
                    <span className="ml-1 inline-block rounded bg-[#F2F8F4] px-1.5 py-0.5 align-middle text-[9.5px] font-bold uppercase tracking-[0.4px] text-[#0F4C3A]">{text(item.tag)}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )
      case 'cta':
        return (
          <button
            key={block.id}
            type="button"
            disabled={cta?.disabled}
            onClick={cta?.onClick}
            className={cn(
              'mt-auto inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-bold tracking-[0.2px] transition-all disabled:cursor-not-allowed disabled:opacity-50',
              block.style === 'primary'
                ? 'bg-[#C5F26A] text-[#073529] shadow-[0_4px_14px_rgba(164,212,71,0.35)] enabled:hover:bg-[#B5E558]'
                : 'border-[1.5px] border-[#D6EBDE] bg-white text-[#0F4C3A] enabled:hover:border-[#0F4C3A] enabled:hover:bg-[#F2F8F4]',
            )}
          >
            {cta?.loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {text(block.label)}
            {block.arrow && !cta?.loading && <ArrowRight className="h-3.5 w-3.5" />}
          </button>
        )
      default:
        // A block type this client does not know yet.
        return null
    }
  }

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-[20px] border px-[26px] pb-[26px] pt-7 font-[Inter] transition-all',
        card.variant === 'featured' &&
          'border-[#073529] bg-gradient-to-b from-[#0F4C3A] to-[#073529] text-white shadow-[0_20px_48px_rgba(15,76,58,0.22),0_0_0_1px_rgba(197,242,106,0.3)] [--card-strong:#fff] md:-translate-y-1.5',
        card.variant === 'default' && 'border-[#E8EDE9] bg-white [--card-strong:#0F4C3A] hover:border-[#D6EBDE] hover:shadow-[0_12px_32px_rgba(15,76,58,0.08)]',
        card.variant === 'muted' && 'border-dashed border-[#D6EBDE] bg-white opacity-80 [--card-strong:#0F4C3A]',
        className,
      )}
    >
      {card.badge && <Badge badge={card.badge} text={text(card.badge.text)} />}
      {card.blocks.map(renderBlock)}
    </div>
  )
}

export default PlanCard
